interface ContentAnalysisResult {
  wordCount: number
  characterCount: number
  characterCountNoSpaces: number
  paragraphCount: number
  sentenceCount: number
  readingTimeMinutes: number
  headingStructure: HeadingInfo[]
  keywordDensity: Map<string, number>
  readabilityScore?: number
  recommendations: string[]
}

interface HeadingInfo {
  level: number
  text: string
  position: number
}

export const analyzeContent = (content: string): ContentAnalysisResult => {
  // Remove HTML tags for analysis
  const plainText = content.replace(/<[^>]*>/g, '')

  // Calculate word count
  const words = plainText.split(/\s+/).filter(word => word.length > 0)
  const wordCount = words.length

  // Calculate character counts
  const characterCount = plainText.length
  const characterCountNoSpaces = plainText.replace(/\s/g, '').length

  // Calculate paragraph count
  const paragraphs = plainText.split(/\n\n+/).filter(p => p.trim().length > 0)
  const paragraphCount = paragraphs.length

  // Calculate sentence count
  const sentences = plainText.split(/[.!?]+/).filter(s => s.trim().length > 0)
  const sentenceCount = sentences.length

  // Calculate reading time (average 200 words per minute)
  const readingTimeMinutes = Math.ceil(wordCount / 200)

  // Extract heading structure
  const headingStructure = extractHeadings(content)

  // Calculate keyword density
  const keywordDensity = calculateKeywordDensity(words)

  // Calculate readability score (simplified Flesch-Kincaid)
  const readabilityScore = calculateReadabilityScore(wordCount, sentenceCount, syllableCount(plainText))

  // Generate recommendations
  const recommendations = generateContentRecommendations({
    wordCount,
    paragraphCount,
    sentenceCount,
    headingStructure,
    readabilityScore,
  })

  return {
    wordCount,
    characterCount,
    characterCountNoSpaces,
    paragraphCount,
    sentenceCount,
    readingTimeMinutes,
    headingStructure,
    keywordDensity,
    readabilityScore,
    recommendations,
  }
}

const extractHeadings = (htmlContent: string): HeadingInfo[] => {
  const headings: HeadingInfo[] = []
  const headingRegex = /<h([1-6])[^>]*>(.*?)<\/h\1>/gi
  let match
  let position = 0

  while ((match = headingRegex.exec(htmlContent)) !== null) {
    headings.push({
      level: parseInt(match[1]),
      text: match[2].replace(/<[^>]*>/g, '').trim(),
      position: position++,
    })
  }

  return headings
}

const calculateKeywordDensity = (words: string[]): Map<string, number> => {
  const keywordMap = new Map<string, number>()
  const stopWords = new Set([
    'the', 'is', 'at', 'which', 'on', 'a', 'an', 'and', 'or', 'but',
    'in', 'with', 'to', 'for', 'of', 'as', 'by', 'that', 'this',
    'it', 'from', 'be', 'are', 'was', 'were', 'been', 'have', 'has',
    'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should',
    'may', 'might', 'can', 'could', 'shall', 'must'
  ])

  words.forEach(word => {
    const normalized = word.toLowerCase().replace(/[^a-z0-9]/g, '')
    if (normalized.length > 2 && !stopWords.has(normalized)) {
      keywordMap.set(normalized, (keywordMap.get(normalized) || 0) + 1)
    }
  })

  // Sort by frequency and return top keywords
  return new Map(
    Array.from(keywordMap.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
  )
}

const syllableCount = (text: string): number => {
  // Simplified syllable counting
  const words = text.toLowerCase().split(/\s+/)
  let totalSyllables = 0

  words.forEach(word => {
    // Remove non-alphabetic characters
    word = word.replace(/[^a-z]/g, '')
    if (word.length === 0) return

    // Count vowel groups as syllables
    const vowelGroups = word.match(/[aeiouy]+/g) || []
    let syllables = vowelGroups.length

    // Adjust for silent e
    if (word.endsWith('e') && syllables > 1) {
      syllables--
    }

    // Minimum of 1 syllable per word
    totalSyllables += Math.max(1, syllables)
  })

  return totalSyllables
}

const calculateReadabilityScore = (words: number, sentences: number, syllables: number): number => {
  if (sentences === 0 || words === 0) return 0

  // Flesch Reading Ease formula
  const score = 206.835 - 1.015 * (words / sentences) - 84.6 * (syllables / words)

  // Bound between 0 and 100
  return Math.max(0, Math.min(100, Math.round(score)))
}

const generateContentRecommendations = (data: {
  wordCount: number
  paragraphCount: number
  sentenceCount: number
  headingStructure: HeadingInfo[]
  readabilityScore?: number
}): string[] => {
  const recommendations: string[] = []

  // Word count recommendations
  if (data.wordCount < 300) {
    recommendations.push('Consider expanding your content to at least 300 words for better SEO')
  } else if (data.wordCount > 3000) {
    recommendations.push('Consider breaking this into multiple posts for better readability')
  }

  // Paragraph recommendations
  if (data.paragraphCount > 0 && data.wordCount / data.paragraphCount > 150) {
    recommendations.push('Break up long paragraphs for better readability')
  }

  // Sentence length recommendations
  if (data.sentenceCount > 0 && data.wordCount / data.sentenceCount > 25) {
    recommendations.push('Some sentences are quite long. Consider breaking them up')
  }

  // Heading structure recommendations
  if (data.headingStructure.length === 0 && data.wordCount > 300) {
    recommendations.push('Add headings to structure your content')
  }

  const h1Count = data.headingStructure.filter(h => h.level === 1).length
  if (h1Count > 1) {
    recommendations.push('Use only one H1 heading per post')
  } else if (h1Count === 0) {
    recommendations.push('Add an H1 heading for better SEO')
  }

  // Check for heading hierarchy
  let previousLevel = 0
  let hasHierarchyIssue = false
  data.headingStructure.forEach(heading => {
    if (previousLevel > 0 && heading.level > previousLevel + 1) {
      hasHierarchyIssue = true
    }
    previousLevel = heading.level
  })
  if (hasHierarchyIssue) {
    recommendations.push('Maintain proper heading hierarchy (don\'t skip heading levels)')
  }

  // Readability recommendations
  if (data.readabilityScore !== undefined) {
    if (data.readabilityScore < 30) {
      recommendations.push('Content is very difficult to read. Simplify sentences and use common words')
    } else if (data.readabilityScore < 50) {
      recommendations.push('Content readability could be improved with simpler language')
    } else if (data.readabilityScore > 80) {
      recommendations.push('Great readability score!')
    }
  }

  return recommendations
}

export const getReadabilityLabel = (score: number): string => {
  if (score >= 90) return 'Very Easy'
  if (score >= 80) return 'Easy'
  if (score >= 70) return 'Fairly Easy'
  if (score >= 60) return 'Standard'
  if (score >= 50) return 'Fairly Difficult'
  if (score >= 30) return 'Difficult'
  return 'Very Difficult'
}

export const getReadabilityColor = (score: number): string => {
  if (score >= 70) return 'text-green-600'
  if (score >= 50) return 'text-yellow-600'
  if (score >= 30) return 'text-orange-600'
  return 'text-red-600'
}